import { useState } from 'react'

import { Editor } from '@tiptap/react'
import BoldIcon from 'remixicon-react/BoldIcon'
import CodeBoxLineIcon from 'remixicon-react/CodeBoxLineIcon'
import CodeLinkIcon from 'remixicon-react/CodeLineIcon'
import DoubleQuotesLIcon from 'remixicon-react/DoubleQuotesLIcon'
import H1Icon from 'remixicon-react/H1Icon'
import H2Icon from 'remixicon-react/H2Icon'
import ItalicIcon from 'remixicon-react/ItalicIcon'
import LinkIcon from 'remixicon-react/LinkIcon'
import LinkUnlinkIcon from 'remixicon-react/LinkUnlinkIcon'
import StrikethroughIcon from 'remixicon-react/StrikethroughIcon'
import UnderlineIcon from 'remixicon-react/UnderlineIcon'
import { twMerge } from 'tailwind-merge'

import { MenuItem } from './BubbleMenuItem'
import { LinkFormDialog } from './LinkFormDialog'

export const TextFormattingMenu = ({
  editor,
  className,
}: {
  editor: Editor
  className?: string
}) => {
  const [linkFormState, setLinkFormState] = useState({
    open: false,
    text: '',
    href: undefined,
  })
  return (
    <>
      <div
        className={twMerge(
          'flex items-center rounded-sm space-x-2 divide-x divide-surface-200/50 py-2 px-3 text-basicSurface-500 bg-surface-50 shadow-lg border border-surface-200/50',
          className,
        )}
      >
        <div className="flex space-x-2">
          <MenuItem
            title="Bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
          >
            <BoldIcon />
          </MenuItem>
          <MenuItem
            title="Italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
          >
            <ItalicIcon />
          </MenuItem>
          <MenuItem
            title="Underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
          >
            <UnderlineIcon />
          </MenuItem>
          <MenuItem
            title="Strike Through"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
          >
            <StrikethroughIcon />
          </MenuItem>
        </div>
        <div className="flex pl-2">
          <MenuItem
            title={editor.isActive('custom-link') ? 'Unlink' : 'Link'}
            onClick={() => {
              const { view, state } = editor
              const { from, to } = view.state.selection
              const text = state.doc.textBetween(from, to, '')
              const node = state.doc.nodeAt(from)
              if (!node) return

              const mark = node.marks?.find(m => m.type.name === 'custom-link')

              setLinkFormState({
                open: true,
                text,
                href: mark?.attrs?.href,
              })
            }}
            isActive={editor.isActive('custom-link')}
          >
            {editor.isActive('custom-link') ? <LinkUnlinkIcon /> : <LinkIcon />}
          </MenuItem>
        </div>
        <div className="flex space-x-2 pl-2">
          <MenuItem
            title="Large Header"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            isActive={editor.isActive('heading', { level: 2 })}
          >
            <H1Icon />
          </MenuItem>
          <MenuItem
            title="Medium Header"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            isActive={editor.isActive('heading', { level: 3 })}
          >
            <H2Icon />
          </MenuItem>
        </div>
        <div className="flex pl-2">
          <MenuItem
            title="Blockquote"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
          >
            <DoubleQuotesLIcon />
          </MenuItem>
        </div>
        <div className="flex space-x-2 pl-2">
          <MenuItem
            title="Inline Code"
            onClick={() => editor.chain().focus().toggleCode().run()}
            isActive={editor.isActive('code')}
          >
            <CodeLinkIcon />
          </MenuItem>
          <MenuItem
            title="Code Block"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
          >
            <CodeBoxLineIcon />
          </MenuItem>
        </div>
      </div>
      <LinkFormDialog
        defaultValues={{
          text: linkFormState.text,
          link: linkFormState.href,
        }}
        onSubmit={data => {
          const { link, text } = data
          if (link) {
            const { from, to } = editor.view.state.selection
            editor
              .chain()
              .setTextSelection({ from, to })
              .insertLink({ href: link, text })
              .run()
          } else {
            editor
              .chain()
              .focus()
              .extendMarkRange('custom-link')
              .unsetLink()
              .run()
          }
          setLinkFormState({
            text: '',
            href: undefined,
            open: false,
          })
        }}
        open={linkFormState.open}
        onClose={() =>
          setLinkFormState({
            text: '',
            href: undefined,
            open: false,
          })
        }
      />
    </>
  )
}
